import { formatDateKey, getZonedDateTimeInfo, parseDateTime } from '@/js/core/utils/formatters';
import {
  DEFAULT_TIMEZONE,
  formatTimezoneLabel,
  shouldShowTimezoneLabel,
} from '@/js/core/utils/timezone';

const CALENDAR_EVENT_SOURCE_TIMEZONE = 'UTC';
export const CALENDAR_EVENT_DISPLAY_MODES = {
  timed: 'timed',
  dateOnly: 'date_only',
  startTimeOnly: 'start_time_only',
};

export function calendarEventStartTime(event) {
  return event?.start_time_utc || event?.start_time || '';
}

export function calendarEventEndTime(event) {
  return event?.end_time_utc || event?.end_time || calendarEventStartTime(event);
}

export function calendarEventSourceTimezone(event) {
  return event?.start_time_utc ? CALENDAR_EVENT_SOURCE_TIMEZONE : DEFAULT_TIMEZONE;
}

export function calendarEventDateKey(event, timezone = DEFAULT_TIMEZONE) {
  if (event?.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    return event.start_date || '';
  }

  return formatDateKey(calendarEventStartTime(event), timezone, calendarEventSourceTimezone(event));
}

export function calendarEventEndDateKey(event, timezone = DEFAULT_TIMEZONE) {
  if (event?.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    return event.end_date || event.start_date || '';
  }

  return formatDateKey(calendarEventEndTime(event), timezone, calendarEventSourceTimezone(event));
}

export function calendarEventDateTimeInfo(event, timezone = DEFAULT_TIMEZONE) {
  return getZonedDateTimeInfo(
    calendarEventStartTime(event),
    timezone,
    calendarEventSourceTimezone(event)
  );
}

export function calendarEventEndDateTimeInfo(event, timezone = DEFAULT_TIMEZONE) {
  return getZonedDateTimeInfo(
    calendarEventEndTime(event),
    timezone,
    calendarEventSourceTimezone(event)
  );
}

export function calendarEventTimezoneLabel(event, timezone = DEFAULT_TIMEZONE) {
  if (event?.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    return '';
  }

  if (!shouldShowTimezoneLabel(timezone)) {
    return '';
  }

  const date = parseDateTime(calendarEventStartTime(event), calendarEventSourceTimezone(event));

  return date ? formatTimezoneLabel(date, timezone) : '';
}

export function calendarEventDisplayTime(event, timezone = DEFAULT_TIMEZONE) {
  if (!event || typeof event !== 'object') {
    return null;
  }

  // The backend's time policy already collapses "no time" events to date_only,
  // so display_mode is the single source of truth for suppressing the time.
  if (event.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    return null;
  }

  const startTime = calendarEventStartTime(event);
  if (!startTime) {
    return null;
  }

  const sourceTimezone = calendarEventSourceTimezone(event);
  const start = getZonedDateTimeInfo(startTime, timezone, sourceTimezone);
  if (!start) {
    return null;
  }

  const end = getZonedDateTimeInfo(calendarEventEndTime(event), timezone, sourceTimezone);
  const showEnd =
    event.display_mode !== CALENDAR_EVENT_DISPLAY_MODES.startTimeOnly &&
    end &&
    start.dateKey !== end.dateKey;
  const date = parseDateTime(startTime, sourceTimezone);

  return {
    label: showEnd ? `${timeLabel(start)}–${timeLabel(end)}` : timeLabel(start),
    timezoneLabel:
      date && shouldShowTimezoneLabel(timezone) ? formatTimezoneLabel(date, timezone) : '',
  };
}

export function calendarEventSortTimestamp(event) {
  const startTime = calendarEventStartTime(event);

  if (startTime) {
    return parseDateTime(startTime, calendarEventSourceTimezone(event))?.getTime() || 0;
  }

  if (event?.start_date) {
    return (
      parseDateTime(`${event.start_date} 00:00:00`, CALENDAR_EVENT_SOURCE_TIMEZONE)?.getTime() || 0
    );
  }

  return 0;
}

function timeLabel(time) {
  const minutes = time.hasMinutes ? `:${time.minutes}` : '';

  return `${time.hours}${minutes} ${time.ampm}`;
}

export function dateKeyInfo(dateKey, timezone = DEFAULT_TIMEZONE) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || ''))) {
    return {
      dateKey: '',
      dayName: '',
      dayNumber: '',
      monthName: '',
      year: null,
      iso: '',
      isToday: false,
    };
  }

  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  return {
    dateKey,
    dayName: date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    dayNumber: day,
    monthName: date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }),
    year,
    iso: dateKey,
    isToday: dateKey === formatDateKey(new Date(), timezone),
  };
}

/**
 * Control display event start time for single and multi-day events
 * @returns {string}
 */
export function calendarEventMultiDayTime(
  event,
  timezone = DEFAULT_TIMEZONE,
  { expandCollapsedDates = false, showTimezone = false } = {}
) {
  if (!event || typeof event !== 'object') {
    return '';
  }

  // Defer to the default renderer for hidden time displays.
  if (!calendarEventDisplayTime(event, timezone)) {
    return '';
  }

  const days = multiDayEntries(event, timezone);
  if (days.length <= 1) {
    return '';
  }

  const first = days[0];
  const currentYear = calendarEventCurrentYear(timezone);
  const sameEveryDay = days.every(
    day => day.startLabel === first.startLabel && day.endLabel === first.endLabel
  );
  const suffix = showTimezone
    ? multiDayTimezoneSuffix(calendarEventTimezoneLabel(event, timezone))
    : '';

  if (sameEveryDay) {
    const last = days[days.length - 1];
    const dateLabel = expandCollapsedDates
      ? calendarRangeDateLabel(first, last, currentYear)
      : `${first.dayName} ${multiDayRangeNumberLabel(first, last)}`;
    const timeRange =
      first.endLabel && first.endLabel !== first.startLabel
        ? `${first.startLabel}–${first.endLabel}`
        : first.startLabel;

    return `${dateLabel}, ${timeRange}${suffix}`;
  }

  const list = days
    .map(day => `${calendarDateLabel(day, currentYear)}, ${day.startLabel}`)
    .join('<br>');

  return `${list}${suffix}`;
}

function multiDayEntries(event, timezone) {
  const source =
    Array.isArray(event.visible_occurrences) && event.visible_occurrences.length
      ? event.visible_occurrences
      : Array.isArray(event.occurrences) && event.occurrences.length
        ? event.occurrences
        : [event];

  const byDay = new Map();

  for (const occurrence of source) {
    const startTime = calendarEventStartTime(occurrence);
    if (!startTime) {
      continue;
    }

    const sourceTimezone = calendarEventSourceTimezone(occurrence);
    const start = getZonedDateTimeInfo(startTime, timezone, sourceTimezone);
    if (!start || byDay.has(start.dateKey)) {
      continue;
    }

    const end = getZonedDateTimeInfo(calendarEventEndTime(occurrence), timezone, sourceTimezone);

    byDay.set(start.dateKey, {
      dateKey: start.dateKey,
      dayName: start.dayName,
      monthName: start.monthName,
      dayNumber: start.dayNumber,
      year: start.year,
      startLabel: timeLabel(start),
      endLabel: end ? timeLabel(end) : '',
    });
  }

  return [...byDay.values()].sort((a, b) =>
    a.dateKey < b.dateKey ? -1 : a.dateKey > b.dateKey ? 1 : 0
  );
}

function multiDayTimezoneSuffix(label) {
  return label ? ` <span class="timezone"><span>${label}</span></span>` : '';
}

/**
 * The calendar year "now", in the given display timezone.
 */
export function calendarEventCurrentYear(timezone = DEFAULT_TIMEZONE) {
  return Number(formatDateKey(new Date(), timezone).slice(0, 4));
}

function dayLabel(day, withYear) {
  return withYear
    ? `${day.dayName} ${day.dayNumber}, ${day.year}`
    : `${day.dayName} ${day.dayNumber}`;
}

/**
 * Format a single date as "Fri 7" — adding ", 2025" only when it isn't the current year.
 */
export function calendarDateLabel(day, currentYear) {
  return dayLabel(day, day.year !== currentYear);
}

/**
 * Format a date range as "Fri 7–Sat 8". The year is added to an end when it isn't the
 * current year; when the range crosses two years both ends always keep their year.
 */
export function calendarRangeDateLabel(start, end, currentYear) {
  const crossesYears = start.year !== end.year;

  return `${dayLabel(start, crossesYears || start.year !== currentYear)}–${dayLabel(
    end,
    crossesYears || end.year !== currentYear
  )}`;
}

function multiDayRangeNumberLabel(start, end) {
  if (start.year !== end.year) {
    return `${start.monthName} ${start.dayNumber}, ${start.year}–${end.monthName} ${end.dayNumber}, ${end.year}`;
  }

  if (start.monthName !== end.monthName) {
    return `${start.monthName} ${start.dayNumber}–${end.monthName} ${end.dayNumber}`;
  }

  return `${start.dayNumber}–${end.dayNumber}`;
}
